import React, { ReactNode, createContext, useState } from 'react';
import { SWRConfig } from 'swr';
import { createState } from 'state-pool';
import { fetchApiHub } from '../../lib/fetch-api-hub';
import { FrontasticState, getFrontasticState, GlobalState } from './FrontasticState';
import { UseCart } from './UseCart';
import { UseAccount } from './UseAccount';
import { UseWishlist } from './UseWishlist';
import { UseAdyen } from './UseAdyen';
import { PageFolderTree, TBreadcrumb } from '@frontastic-engbers/types/engbers-custom';
import { SHOP_ENGBERS_KEY } from '@frontastic-engbers/helpers/utils/mapShop';

const initialState: FrontasticState = {
  useCart: {} as UseCart,
  useAccount: {} as UseAccount,
  useWishlist: {} as UseWishlist,
  useAdyen: {} as UseAdyen,
  projectInfo: {
    name: '',
    projectId: '',
    siteUrl: '',
    env: '',
  },
  structuredBreadcrumb: [],
  setStructuredBreadcrumb: () => {},
  shop: '',
  categoryTree: undefined,
};

const globalState: GlobalState = {
  isFlyoutCartOpen: false,
  flyoutAlternativeProductsConfig: undefined,
};

export const FrontasticContext = createContext<FrontasticState>(initialState);
export const GlobalStateContext = createState<GlobalState>(globalState);

interface IFrontasticProvider {
  children: ReactNode;
  context: {
    type: 'pwa' | 'shop';
    shop?: string;
    categoryTree?: PageFolderTree;
  }
}

export const FrontasticProvider: React.FC<IFrontasticProvider> = ({ children, context }) => {
  const [structuredBreadcrumb, setStructuredBreadcrumb] = useState<TBreadcrumb[]>([]);
  const staticState = getFrontasticState(context.type);

  const state: FrontasticState = {
    ...staticState,
    structuredBreadcrumb,
    setStructuredBreadcrumb,
    shop: context.shop ?? SHOP_ENGBERS_KEY,
    categoryTree: context.categoryTree,
  };

  return (
    <SWRConfig value={{ fetcher: fetchApiHub }}>
      <FrontasticContext.Provider value={state}>{children}</FrontasticContext.Provider>
    </SWRConfig>
  );
};

const checkContext = (context: FrontasticState) => {
  if (!context) {
    throw new Error('Expected to be wrapped in FrontasticProvider');
  }
};

export const useCart = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  return context.useCart;
};

export const useAccount = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  return context.useAccount;
};

export const useWishlist = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  return context.useWishlist;
};

export const useAdyen = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  return context.useAdyen;
};

export const setProjectInfo = (projectInfo) => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  context.projectInfo = projectInfo;
};

export const getProjectInfo = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);
  return context.projectInfo;
};

export const useCategoryTree = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);

  return context.categoryTree;
};

export function useShop() {
  const context = React.useContext(FrontasticContext);
  checkContext(context);

  return context.shop;
}

export const useStructuredBreadcrumb = () => {
  const context = React.useContext(FrontasticContext);
  checkContext(context);

  return {
    structuredBreadcrumb: context.structuredBreadcrumb,
    setStructuredBreadcrumb: context.setStructuredBreadcrumb,
  };
};
